"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/axios";

// Paths that should NEVER be blocked (admin, auth, dedicated mode pages)
const EXEMPT_PATHS = ["/maintenance", "/coming-soon", "/admin", "/auth"];

async function fetchSiteMode() {
    try {
        const { data } = await axiosInstance.get("/settings/mode");
        const payload = data?.data ?? data;
        return {
            maintenanceMode: Boolean(payload?.maintenanceMode),
            comingSoonMode: Boolean(payload?.comingSoonMode),
        };
    } catch {
        return { maintenanceMode: false, comingSoonMode: false };
    }
}

/**
 * SiteModeGuard
 *
 * Drop this in the (client) layout. It silently fetches the site's
 * maintenanceMode / comingSoonMode flags and redirects the client
 * to the appropriate page when either flag is active.
 *
 * Priority: maintenanceMode > comingSoonMode
 */
export function SiteModeGuard({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();

    const { data } = useQuery({
        queryKey: ["site-mode"],
        queryFn: fetchSiteMode,
        staleTime: 1000 * 60, // Re-check every 60s
        refetchOnWindowFocus: true,
    });

    const isExempt = EXEMPT_PATHS.some((p) => pathname?.startsWith(p));

    useEffect(() => {
        if (!data || isExempt) return;

        if (data.maintenanceMode) {
            router.replace("/maintenance");
        } else if (data.comingSoonMode) {
            router.replace("/coming-soon");
        }
    }, [data, isExempt, router]);

    // Immediately block render while redirecting (prevents flash of content)
    if (!isExempt && data) {
        if (data.maintenanceMode || data.comingSoonMode) {
            return null;
        }
    }

    return <>{children}</>;
}
